import styled from 'styled-components';
import { useFormContext, useFieldArray } from 'react-hook-form';

import { ExternalColumnType, GetMicrostoreColumnQuery } from 'data/__generated__';
import { SynchronizePlanogramWithNuButton } from 'pages/MicrostoreColumns/MicrostoreColumnDetails/SynchronizePlanogramWithNuButton';
import { SynchronizePlanogramWithSenseiButton } from 'pages/MicrostoreColumns/MicrostoreColumnDetails/SynchronizePlanogramWithSenseiButton';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { SynchronizePlanogramWithNayaxButton } from 'pages/MicrostoreColumns/MicrostoreColumnDetails/SynchronizePlanogramWithNuNayax';
import { DetailFormCheckbox } from 'components/DetailsView/DetailFormCheckbox';
import { useEffect } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';

export const IntegrationsSection = ({
    microstoreColumn,
    isDirty,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
    isDirty: boolean;
}) => {
    const { register, watch, setValue, control } = useFormContext();
    const externalColumnType = watch('externalColumnType', microstoreColumn.externalColumnType);

    const {
        fields: intervalFields,
        append: appendInterval,
        remove: removeInterval,
    } = useFieldArray({
        control,
        name: 'externalColumnConfig.nayaxMDBIntervals',
    });

    const isNayaxType = [ExternalColumnType.NayaxNeocorner].includes(externalColumnType);

    const defaultTypeOption = { value: ExternalColumnType.Default, label: 'Default' };
    const nuTypeOption = { value: ExternalColumnType.Nu, label: 'Nu' };
    const digitizTypeOption = { value: ExternalColumnType.Digitiz, label: 'Digitiz' };
    const senseiTypeOption = { value: ExternalColumnType.Sensei, label: 'Sensei' };
    const nayaxTypeOption = { value: ExternalColumnType.Nayax, label: 'Nayax café TOTEM (DEPRECATED)' };
    const nayaxNeocornerTypeOption = { value: ExternalColumnType.NayaxNeocorner, label: 'Nayax Neocorner' };
    const vendonTypeOption = { value: ExternalColumnType.Vendon, label: 'Vendon' };
    const odooTypeOption = { value: ExternalColumnType.Odoo, label: 'Odoo' };

    const extarnalColumnTypesOptions =
        microstoreColumn.isSiteSecured && microstoreColumn.canBeSecured
            ? [
                  defaultTypeOption,
                  nuTypeOption,
                  digitizTypeOption,
                  senseiTypeOption,
                  nayaxTypeOption,
                  nayaxNeocornerTypeOption,
                  vendonTypeOption,
                  odooTypeOption,
              ]
            : [defaultTypeOption, nayaxTypeOption, nayaxNeocornerTypeOption, vendonTypeOption];

    useEffect(() => {
        if (externalColumnType === ExternalColumnType.Default) {
            setValue('externalColumnId', null);
        }
        if (!isNayaxType) {
            setValue('externalColumnConfig.nayaxMDBIntervals', []); // Use this to reset the intervals before setting the config to null
            setValue('externalColumnConfig', null);
        }
    }, [externalColumnType]);

    return (
        <SectionContainer title="Intégrations" isInitiallyOpen>
            <Content>
                <FormRow>
                    <DetailFormSelect<ExternalColumnType>
                        label="Type d'intégration externe"
                        name="externalColumnType"
                        defaultValue={microstoreColumn.externalColumnType}
                        options={extarnalColumnTypesOptions}
                    />
                    {externalColumnType != ExternalColumnType.Default && (
                        <DetailFormValue
                            label="ID de colonne externe"
                            {...register('externalColumnId')}
                            defaultValue={microstoreColumn.externalColumnId || ''}
                            placeholder="Entrez l'ID externe..."
                        />
                    )}
                    {externalColumnType === ExternalColumnType.Nu && !isDirty && (
                        <ButtonsContainer>
                            <SynchronizePlanogramWithNuButton
                                microstoreColumnId={microstoreColumn._id}
                                disabled={isDirty}
                            />
                        </ButtonsContainer>
                    )}
                    {externalColumnType === ExternalColumnType.Sensei && !isDirty && (
                        <ButtonsContainer>
                            <SynchronizePlanogramWithSenseiButton
                                microstoreColumnId={microstoreColumn._id}
                                disabled={isDirty}
                            />
                        </ButtonsContainer>
                    )}
                    {externalColumnType === ExternalColumnType.NayaxNeocorner && (
                        <ButtonsContainer>
                            <SynchronizePlanogramWithNayaxButton
                                microstoreColumnId={microstoreColumn._id}
                                disabled={isDirty}
                            />
                        </ButtonsContainer>
                    )}
                </FormRow>

                {isNayaxType && (
                    <StyledColumnsSectionContainer>
                        <SectionColumn>
                            <DetailFormCheckbox
                                label="Envoyer les prix à Nayax"
                                name="shouldSendNayaxPrices"
                                defaultChecked={microstoreColumn.shouldSendNayaxPrices}
                            />
                        </SectionColumn>
                        <SectionColumn>
                            <NayaxMDBIntervalsContainer>
                                <IntervalsTitle>Plages MDB Nayax</IntervalsTitle>
                                {intervalFields.length === 0 && (
                                    <NoIntervalText>
                                        Aucune plage définie — tous les codes MDB sont acceptés sur cette colonne.
                                    </NoIntervalText>
                                )}
                                {intervalFields.map((field, index) => (
                                    <IntervalCard key={field.id}>
                                        <IntervalInputSection>
                                            <InputLabel>Min</InputLabel>
                                            <DetailFormValue
                                                {...register(`externalColumnConfig.nayaxMDBIntervals.${index}.min`, {
                                                    required: true,
                                                    valueAsNumber: true,
                                                    min: 0,
                                                })}
                                                type="number"
                                                step="1"
                                                min="0"
                                                placeholder="0"
                                                width="80px"
                                            />
                                        </IntervalInputSection>
                                        <IntervalInputSection>
                                            <InputLabel>Max</InputLabel>
                                            <DetailFormValue
                                                {...register(`externalColumnConfig.nayaxMDBIntervals.${index}.max`, {
                                                    required: true,
                                                    valueAsNumber: true,
                                                    min: 0,
                                                    validate: (value) => {
                                                        const min = watch(
                                                            `externalColumnConfig.nayaxMDBIntervals.${index}.min`,
                                                        );
                                                        return value >= min || 'Max doit être ≥ Min';
                                                    },
                                                })}
                                                type="number"
                                                step="1"
                                                min="0"
                                                placeholder="0"
                                                width="80px"
                                            />
                                        </IntervalInputSection>
                                        <DeleteButton type="button" onClick={() => removeInterval(index)}>
                                            <FaTimes />
                                        </DeleteButton>
                                    </IntervalCard>
                                ))}
                                <AddSection>
                                    <AddButton type="button" onClick={() => appendInterval({ min: 0, max: 0 })}>
                                        <FaPlus />
                                    </AddButton>
                                </AddSection>
                            </NayaxMDBIntervalsContainer>
                        </SectionColumn>
                    </StyledColumnsSectionContainer>
                )}
            </Content>
        </SectionContainer>
    );
};

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const FormRow = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-start;

    & > * {
        flex: 1;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const NayaxMDBIntervalsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const IntervalsTitle = styled.span`
    font-weight: 600;
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;

const NoIntervalText = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
    opacity: 0.7;
`;

const IntervalCard = styled.div`
    padding: 5px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    display: flex;
    align-items: flex-end;
    gap: 10px;
`;

const IntervalInputSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const InputLabel = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
    margin-bottom: 5px;
`;

const AddSection = styled.div`
    margin-top: 2px;
`;

const AddButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.ctaPrimaryColor};
    color: white;
    cursor: pointer;
    flex-shrink: 0;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    font-size: 16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: auto;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;

const StyledColumnsSectionContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    width: 100%;
`;
